import React, { useState, useEffect, useCallback } from 'react';
import { FaArrowLeft, FaEdit, FaTrash, FaSave, FaChevronDown, FaChevronUp, FaPlus } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, updateDoc, deleteDoc, addDoc, Timestamp } from 'firebase/firestore';
import { db } from '../App';
import { useFireAuth } from '../FireAuthContext';
import './AdminNotices.css';

const AdminNotices = () => {
  const { user } = useFireAuth();
  const [notices, setNotices] = useState([]);
  const [editingNotice, setEditingNotice] = useState(null);
  const [newNotice, setNewNotice] = useState({ title: '', timestamp: '', content: '' });
  const [expandedNotices, setExpandedNotices] = useState({});
  const navigate = useNavigate();

  const fetchNotices = useCallback(async () => {
    const noticesCollection = collection(db, 'notices');
    const noticesSnapshot = await getDocs(noticesCollection);
    const noticesList = noticesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      timestamp: doc.data().timestamp.toDate()
    }));
    setNotices(noticesList);
  }, []);

  useEffect(() => {
    fetchNotices();
  }, [fetchNotices]);

  const handleEditNotice = (notice) => {
    setEditingNotice(notice);
  };

  const handleDeleteNotice = async (noticeId) => {
    if (window.confirm('Are you sure you want to delete this notice?')) {
      await deleteDoc(doc(db, 'notices', noticeId));
      setNotices(prevNotices => prevNotices.filter(notice => notice.id !== noticeId));
    }
  };

  const handleSaveNotice = async () => {
    if (editingNotice) {
      const noticeDocRef = doc(db, 'notices', editingNotice.id);
      await updateDoc(noticeDocRef, {
        ...editingNotice,
        timestamp: Timestamp.fromDate(new Date(editingNotice.timestamp))
      });
      setNotices(prevNotices => prevNotices.map(notice => (notice.id === editingNotice.id ? editingNotice : notice)));
      setEditingNotice(null);
    } else {
      const newNoticeDoc = await addDoc(collection(db, 'notices'), {
        ...newNotice,
        timestamp: Timestamp.fromDate(new Date(newNotice.timestamp))
      });
      setNotices([...notices, { id: newNoticeDoc.id, ...newNotice }]);
      setNewNotice({ title: '', timestamp: '', content: '' });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (editingNotice) {
      setEditingNotice({ ...editingNotice, [name]: value });
    } else {
      setNewNotice({ ...newNotice, [name]: value });
    }
  };

  const handleToggleExpand = (noticeId) => {
    setExpandedNotices(prevState => ({
      ...prevState,
      [noticeId]: !prevState[noticeId]
    }));
  };

  const handleNewNotice = () => {
    setEditingNotice(null);
    setNewNotice({ title: '', timestamp: '', content: '' });
  };

  if (!user) {
    return <div>Loading...</div>;
  }

  return (
    <div className="page-container">
      <div className="admin-notices-container">
        <FaArrowLeft className="admin-notices-back-arrow" onClick={() => navigate(-1)} />
        <h2>Notices Admin</h2>
        <div className="notices-list">
          {notices.map(notice => (
            <div key={notice.id} className="notice-item">
              <div className="notice-header" onClick={() => handleToggleExpand(notice.id)}>
                <h3>{notice.title}</h3>
                <div className="notice-actions">
                  <button onClick={(e) => { e.stopPropagation(); handleEditNotice(notice); }}><FaEdit /></button>
                  <button onClick={(e) => { e.stopPropagation(); handleDeleteNotice(notice.id); }}><FaTrash /></button>
                  <button>
                    {expandedNotices[notice.id] ? <FaChevronUp /> : <FaChevronDown />}
                  </button>
                </div>
              </div>
              {expandedNotices[notice.id] && (
                <>
                  <p>{notice.timestamp.toLocaleString()}</p>
                  <div dangerouslySetInnerHTML={{ __html: notice.content }} />
                </>
              )}
            </div>
          ))}
        </div>
        <div className="notice-form">
          <h3>{editingNotice ? 'Edit Notice' : 'New Notice'}</h3>
          <input
            type="text"
            name="title"
            value={editingNotice ? editingNotice.title : newNotice.title}
            onChange={handleInputChange}
            placeholder="Title"
          />
          <input
            type="datetime-local"
            name="timestamp"
            value={editingNotice ? editingNotice.timestamp.toISOString().slice(0, 16) : newNotice.timestamp}
            onChange={handleInputChange}
          />
          <textarea
            name="content"
            value={editingNotice ? editingNotice.content : newNotice.content}
            onChange={handleInputChange}
            placeholder="Content (HTML allowed)"
          />
          <button onClick={handleSaveNotice}><FaSave /> Save</button>
          {editingNotice && (
            <button onClick={handleNewNotice}><FaPlus /> New Notice</button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminNotices;
